import { useState } from "react";
import {
  View,
  ScrollView,
  Pressable,
  Text,
  Modal,
  useWindowDimensions,
} from "react-native";
import { ShoppingBag, Check, X } from "lucide-react-native";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useStytch } from "@src/packages/api/useStytch";
import { getIdsFromMetadata } from "@src/packages/api/louise-api";
import { useTranslations } from "@src/packages/i18n";
import { fetchTestingCheckoutSecret } from "../hooks/testingCheckoutApi";
import { EMBER } from "../../onboarding/theme";
import { BrownButton } from "@louise/moon-ui";

import { usePressCapture } from "@src/packages/analytics/usePressCapture";
/**
 * Lazily load Stripe.js. Calling loadStripe() injects the js.stripe.com script
 * and fires Stripe's m.stripe.com telemetry beacon, so we hold off until the
 * user actually starts a checkout instead of doing it at module load. Memoized
 * so repeat checkouts reuse the same instance.
 */
let stripePromise: ReturnType<typeof loadStripe> | null = null;
function getStripe() {
  if (!stripePromise) {
    stripePromise = loadStripe(
      process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? "",
    );
  }
  return stripePromise;
}

/** The pay form rendered inside <Elements>; confirms the PaymentIntent in-page. */
function WebCheckoutForm({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: () => void;
}) {
  const t = useTranslations();
  const stripe = useStripe();
  const elements = useElements();
  const { height } = useWindowDimensions();
  const [submitting, setSubmitting] = useState(false);

  return (
    <View>
      {/* Card fields scroll; the Order button below stays pinned and visible. */}
      <ScrollView
        style={{ maxHeight: Math.round(height * 0.5) }}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 16 }}
        showsVerticalScrollIndicator
      >
        <PaymentElement />
      </ScrollView>
      <View
        style={{ paddingHorizontal: 20, paddingTop: 12, paddingBottom: 20 }}
      >
        <BrownButton
          label={t("learn.test_detail.order_now")}
          loading={submitting}
          disabled={!stripe || !elements}
          onPress={async () => {
            if (!stripe || !elements) return;
            setSubmitting(true);
            const { error } = await stripe.confirmPayment({
              elements,
              redirect: "if_required",
            });
            setSubmitting(false);
            if (error) onError();
            else onSuccess();
          }}
          leading={<ShoppingBag size={16} color="#fff" />}
        />
      </View>
    </View>
  );
}

/**
 * Web variant of the Order button. Gets a PaymentIntent from the dashboard API
 * and confirms it in-page with the Stripe payment element (card, plus Apple Pay
 * / Google Pay on supported browsers). No redirect, no native module.
 */
export default function OrderKitButton({ productId }: { productId: string }) {
  const t = useTranslations();
  const stytch = useStytch();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const capturePress = usePressCapture("OrderKitButton");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);

  const startCheckout = async () => {
    setError(false);
    setLoading(true);
    // Start loading Stripe.js now (in parallel with the PaymentIntent fetch)
    // rather than at module load, so the script + telemetry only run for users
    // who actually begin a checkout.
    getStripe();
    const token = stytch.session.getTokens()?.session_jwt;
    const { tenantId } = getIdsFromMetadata(
      stytch.user.getSync()?.trusted_metadata ?? {},
    );
    const secret = await fetchTestingCheckoutSecret({
      token,
      tenantId,
      productId,
    });
    setLoading(false);
    if (!secret) {
      setError(true);
      return;
    }
    setClientSecret(secret);
    setOpen(true);
  };

  return (
    <View>
      <BrownButton
        label={
          done
            ? t("learn.test_detail.order_success")
            : t("learn.test_detail.order_now")
        }
        loading={loading}
        disabled={done}
        disabledOpacity={1}
        variant={done ? "soft" : "solid"}
        onPress={startCheckout}
        leading={
          done ? (
            <Check size={16} color={EMBER.colors.text} />
          ) : (
            <ShoppingBag size={16} color="#fff" />
          )
        }
        style={{ marginTop: 28 }}
      />
      {error ? (
        <Text
          className="font-dm-sans"
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "#B4524A",
            textAlign: "center",
          }}
        >
          {t("learn.test_detail.order_error")}
        </Text>
      ) : null}

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: "rgba(20, 12, 9, 0.4)",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <View
            style={{
              width: "100%",
              maxWidth: 440,
              maxHeight: "90%",
              backgroundColor: "#fff",
              borderRadius: 20,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                justifyContent: "flex-end",
                paddingTop: 12,
                paddingHorizontal: 16,
              }}
            >
              <Pressable
                onPress={() => {
                  // X-icon with no visible copy to name it.
                  capturePress("close_order_kit");
                  setOpen(false);
                }}
                hitSlop={8}
              >
                <X size={18} color={EMBER.colors.textLight} />
              </Pressable>
            </View>
            {clientSecret ? (
              <Elements stripe={getStripe()} options={{ clientSecret }}>
                <WebCheckoutForm
                  onSuccess={() => {
                    setOpen(false);
                    setDone(true);
                  }}
                  onError={() => setError(true)}
                />
              </Elements>
            ) : null}
          </View>
        </View>
      </Modal>
    </View>
  );
}
